import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Search, SlidersHorizontal, Home as HomeIcon, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import SEO from "@/components/SEO";
import TourCard, { TourCardSkeleton } from "@/components/TourCard";

export default function Tours() {
  const [params, setParams] = useSearchParams();
  const [tours, setTours] = useState(null);
  const [destinations, setDestinations] = useState([]);
  const [search, setSearch] = useState(params.get("search") || "");
  const type = params.get("type") || "";
  const destination = params.get("destination") || "";
  const region = params.get("region") || "";

  useEffect(() => { api.get("/destinations").then((r) => setDestinations(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    setTours(null);
    const q = {};
    if (type) q.type = type;
    if (destination) q.destination = destination;
    if (region) q.region = region;
    if (params.get("search")) q.search = params.get("search");
    api.get("/tours", { params: q }).then((r) => setTours(r.data)).catch(() => setTours([]));
  }, [type, destination, region, params]);

  const update = (key, val) => {
    const next = new URLSearchParams(params);
    if (val) next.set(key, val); else next.delete(key);
    if (key === "region") next.delete("destination");
    setParams(next);
  };
  const onSearch = (e) => { e.preventDefault(); update("search", search); };

  const india = tours ? tours.filter((t) => t.region === "India") : [];
  const intl = tours ? tours.filter((t) => t.region !== "India") : [];

  const Section = ({ title, items }) => items.length ? (
    <div className="mb-12">
      <h2 className="mb-6 font-display text-2xl font-bold text-navy sm:text-3xl">{title} <span className="text-brandorange">Tour Packages</span></h2>
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">{items.map((t) => <TourCard key={t.id} tour={t} />)}</div>
    </div>
  ) : null;

  return (
    <>
      <SEO title="All Tour Packages | Happy Holidays Wale" description="Browse premium FIT and Group tour packages across India and International destinations." />
      <div className="bg-navy pb-16 pt-10" style={{ backgroundColor: "#002244" }}>
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <nav className="mb-4 flex items-center gap-2 text-sm text-white/60">
            <Link to="/" className="flex items-center gap-1 hover:text-white"><HomeIcon className="h-4 w-4" /> Home</Link>
            <ChevronRight className="h-4 w-4" /> <span className="text-white">Tours{region && ` · ${region}`}</span>
          </nav>
          <h1 className="font-display text-4xl font-extrabold text-white sm:text-5xl">Find Your Perfect Getaway</h1>
          <p className="mt-3 max-w-xl text-white/70">Explore curated FIT & Group holiday packages designed for unforgettable experiences.</p>
          <form onSubmit={onSearch} className="mt-6 flex max-w-xl overflow-hidden rounded-full bg-white p-1.5">
            <input data-testid="tours-search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search destinations or tours..." className="flex-1 bg-transparent px-4 text-navy outline-none" />
            <button data-testid="tours-search-btn" className="btn-gradient flex items-center gap-2 rounded-full px-6 py-2.5 font-bold"><Search className="h-4 w-4" /> Search</button>
          </form>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-10 md:px-8">
        <div className="mb-8 flex flex-wrap items-center gap-3">
          <span className="flex items-center gap-2 text-sm font-semibold text-navy"><SlidersHorizontal className="h-4 w-4" /> Filters:</span>
          {["", "India", "International"].map((r) => (
            <button key={r || "allr"} data-testid={`filter-region-${r || "all"}`} onClick={() => update("region", r)} className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${region === r ? "brand-gradient text-white" : "border border-gray-200 text-navy hover:border-brandorange"}`}>{r === "" ? "All Regions" : r}</button>
          ))}
          {["", "FIT", "GROUP"].map((t) => (
            <button key={t || "all"} data-testid={`filter-type-${t || "all"}`} onClick={() => update("type", t)} className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${type === t ? "brand-gradient text-white" : "border border-gray-200 text-navy hover:border-brandorange"}`}>{t === "" ? "All Types" : t === "FIT" ? "FIT" : "Group"}</button>
          ))}
          <select data-testid="filter-destination" value={destination} onChange={(e) => update("destination", e.target.value)} className="rounded-full border border-gray-200 px-4 py-2 text-sm font-semibold text-navy outline-none">
            <option value="">All Destinations</option>
            {destinations.filter((d) => !region || d.region === region).map((d) => <option key={d.id} value={d.slug}>{d.name}</option>)}
          </select>
          {(type || destination || region || params.get("search")) && <button data-testid="clear-filters" onClick={() => setParams({})} className="text-sm font-semibold text-brandred hover:underline">Clear all</button>}
        </div>

        {tours === null ? (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">{Array.from({ length: 6 }).map((_, i) => <TourCardSkeleton key={i} />)}</div>
        ) : tours.length === 0 ? (
          <div className="py-24 text-center" data-testid="tours-empty">
            <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-gray-100"><Search className="h-9 w-9 text-gray-400" /></div>
            <h3 className="font-display text-2xl font-bold text-navy">No tours found</h3>
            <p className="mt-2 text-gray-500">Try adjusting your filters or search terms.</p>
          </div>
        ) : (
          <>
            <p className="mb-6 text-sm text-gray-500" data-testid="tours-count">{tours.length} package{tours.length !== 1 ? "s" : ""} found</p>
            <Section title="India" items={india} />
            <Section title="International" items={intl} />
          </>
        )}
      </div>
    </>
  );
}
